import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const techsbookApi = createApi({
  reducerPath: "techsbook/api",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_API_URL_DEVELOP}`,
    // baseUrl: `https://customer-api.techsbookdemo.com/api/v1`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("techsbook_customer_portal_token");
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
        headers.set("time", String(Date.now()));
      }

      return headers;
    },
  }),
  keepUnusedDataFor: 0.0001,
  endpoints: (build) => ({
    login: build.query({
      query: (body) => ({
        url: `/auth/login`,
        body,
        method: "POST",
      }),
    }),
    forgotPassword: build.query({
      query: (body) => ({
        url: `/auth/reset-password`,
        body,
        method: "POST",
      }),
    }),
    verify: build.query({
      query: (body) => ({
        url: `/auth/verify`,
        body,
        method: "POST",
      }),
    }),
    setProfileDetails: build.query({
      query: (body) => ({
        url: `/auth/set-password`,
        body,
        method: "POST",
      }),
    }),
    checkToken: build.query({
      query: (body) => ({
        url: `/auth/check-provider-customer-credentials`,
        body,
        method: "POST",
      }),
    }),
    register: build.query({
      query: (body) => ({
        url: `/auth/register`,
        body,
        method: "POST",
      }),
    }),
    getCountries: build.query({
      query: () => ({
        url: `/entity/countries`,
        method: "GET",
      }),
    }),
    getBusinessUnits: build.query({
      query: (params) => ({
        url: `/entity/business-units`,
        method: "GET",
      }),
    }),
    getInstallations: build.query({
      query: () => ({
        url: `/installations`,
        method: "GET",
      }),
    }),
    getProviders: build.query({
      query: () => ({
        url: `/provider-companies`,
        method: "GET",
      }),
    }),
    completeRegister: build.query({
      query: (body) => ({
        url: `/complete-registration`,
        method: "POST",
        body,
      }),
    }),
    getAccount: build.query({
      query: () => ({
        url: `/account/index`,
        method: "GET",
      }),
    }),
    getPrivacyPolicy: build.query({
      query: () => ({
        url: `/privacy`,
        method: "GET",
      }),
    }),
    setInstallations: build.query({
      query: (body) => ({
        url: `/account/update-installation`,
        body,
        method: "POST",
      }),
    }),
    setProviders: build.query({
      query: (body) => ({
        url: `/account/update-provider`,
        body,
        method: "POST",
      }),
    }),
    updateProfile: build.query({
      query: (body) => ({
        url: `/account/update-profile`,
        body,
        method: "POST",
      }),
    }),
    updateUserPhoto: build.mutation({
      query: (body) => ({
        url: `/account/update-profile-image`,
        body,
        method: "POST",
      }),
    }),
    updateEmail: build.mutation({
      query: (body) => ({
        url: `/account/change-email`,
        body,
        method: "POST",
      }),
    }),
    profileVerifyEmail: build.mutation({
      query: (body) => ({
        url: `/account/verify`,
        body,
        method: "POST",
      }),
    }),
    updateProfilePassword: build.mutation({
      query: (body) => ({
        url: `/account/change-password`,
        body,
        method: "POST",
      }),
    }),
    getLocations: build.query({
      query: () => ({
        url: `/account/locations`,
        method: "GET",
      }),
      providesTags: ["getLocations"],
    }),
    createLocation: build.mutation({
      query: (body) => ({
        url: `/account/locations/create`,
        body,
        method: "POST",
      }),
      invalidatesTags: [`getLocations`],
    }),
    updateLocation: build.mutation({
      query: (body) => ({
        url: `/account/locations/${body.id}/update`,
        body,
        method: "POST",
      }),
      invalidatesTags: [`getLocations`],
    }),
    setMainLocation: build.mutation({
      query: (body) => ({
        url: `/account/locations/${body?.id}/make-a-main`,
        body,
        method: "POST",
      }),
      invalidatesTags: [`getLocations`],
    }),
    deleteLocation: build.mutation({
      query: (body) => ({
        url: `/account/locations/${body?.id}/delete`,
        body,
        method: "POST",
      }),
      invalidatesTags: [`getLocations`],
    }),
    deletePaymentMethod: build.mutation({
      query: (body) => ({
        url: `/payment-method/delete/${body?.id}`,
        body,
        method: "POST",
      }),
      invalidatesTags: [`getPaymentList`],
    }),
    deleteProfileImage: build.mutation({
      query: () => ({
        url: `/account/delete-profile-image`,
        method: "POST",
      }),
    }),
    setupIntent: build.mutation({
      query: () => ({
        url: `/payment-method/create-setup-intent`,
        method: "POST",
      }),
    }),
    addPayment: build.mutation({
      query: (payment_method_id) => ({
        url: `/payment-method/add`,
        method: "POST",
        body: { payment_method_id },
      }),
    }),
    getPaymentList: build.query({
      query: () => ({
        url: `/payment-method/index`,
        method: "GET",
      }),
    }),
    getNotification: build.query({
      query: (params) => ({
        url: `/notifications?${params}`,
        method: "GET",
      }),
    }),
    getUnreadNotification: build.query({
      query: (params) => ({
        url: `/notifications?read=0&${params}`,
        method: "GET",
      }),
    }),
    markNotification: build.query({
      query: ({params, body}) => ({
        url: `/notifications/${params}/mark-as-read`,
        method: "POST",
      }),
    }),
    getFaq: build.query({
      query: (params) => ({
        url: `faqs?${params}`,
        method: "GET",
      }),
    }),
    getCreditHistories: build.query({
      query: (params) => ({
          url: `/account/credit-histories?${params}`,
          method: "GET",
      }),
    }),
    getTerms: build.query({
      query: () => ({
        url: `terms`,
        method: "GET",
      }),
    }),
    getAgreement: build.query({
      query: (params) => ({
        url: `auth/${params}/agreement`,
        method: "GET",
      }),
    }),
    getStripeKey: build.query({
      query: () => ({
        url: `payment-method/get-public-key`,
        method: "GET",
      }),
    }),
    getCustomerAppQrCode: build.query({
      query: () => ({
        url: `/customer-app/qr-code`,
        method: "GET",
      }),
    }),
    affirmPaymentIntent: build.mutation({
      query: (data) => ({
        url: `/jobs/affirm/create-payment-intent/${data?.job_id}/${data?.paymentId}?amount=${data?.amount}`,
        method: "POST",
      }),
    }),
    updatePaymentMethod: build.mutation({
      query: (data) => ({
        url: `/jobs/${data?.job_id}/${data?.paymentId}/change-payment-method`,
        method: "POST",
        body: data
      }),
    }),
    logout: build.mutation({
        query: () => ({
          url: `/logout`,
          method: "POST",
        }),
    }),
  }),
});

export const {
  useLazyLoginQuery,
  useLazyForgotPasswordQuery,
  useLazyVerifyQuery,
  useLazySetProfileDetailsQuery,
  useLazyCheckTokenQuery,
  useLazyRegisterQuery,
  useLazyGetCountriesQuery,
  useLazyGetBusinessUnitsQuery,
  useGetInstallationsQuery,
  useLazyCompleteRegisterQuery,
  useLazyGetInstallationsQuery,
  useGetProvidersQuery,
  useLazyGetProvidersQuery,
  useGetAccountQuery,
  useLazyGetAccountQuery,
  useLazyGetPrivacyPolicyQuery,
  useLazySetInstallationsQuery,
  useLazySetProvidersQuery,
  useLazyUpdateProfileQuery,
  useUpdateUserPhotoMutation,
  useUpdateEmailMutation,
  useProfileVerifyEmailMutation,
  useUpdateProfilePasswordMutation,
  useGetLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useSetMainLocationMutation,
  useDeleteLocationMutation,
  useDeletePaymentMethodMutation,
  useDeleteProfileImageMutation,
  useSetupIntentMutation,
  useAddPaymentMutation,
  useLazyGetPaymentListQuery,
  useLazyGetFaqQuery,
  useLazyGetCreditHistoriesQuery,
  useLazyGetTermsQuery,
  useLazyGetAgreementQuery,
  useLazyGetStripeKeyQuery,
  useLazyGetCustomerAppQrCodeQuery,
  useLazyGetNotificationQuery,
  useLazyGetUnreadNotificationQuery,
  useLazyMarkNotificationQuery,
  useAffirmPaymentIntentMutation,
  useUpdatePaymentMethodMutation,
  useLogoutMutation,
} = techsbookApi;
